import { Component, type ReactNode } from 'react';

const GAME_STORAGE_KEY = 'twoDownAutoPlus_gameState';

interface Props { children: ReactNode }
interface State { error: Error | null; reported: boolean }

function postCrashReport(error: Error, componentStack: string) {
  try {
    fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        message: error.message,
        stack: error.stack ?? '',
        componentStack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        ts: new Date().toISOString(),
      }),
    }).catch(() => {});
  } catch {}
}

function hasSavedRound(): boolean {
  try {
    return !!localStorage.getItem(GAME_STORAGE_KEY);
  } catch {
    return false;
  }
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reported: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary] caught:', error, info.componentStack);
    if (!this.state.reported) {
      this.setState({ reported: true });
      postCrashReport(error, info.componentStack);
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const roundSaved = hasSavedRound();

    return (
      <div style={{
        position: 'fixed', inset: 0,
        backgroundColor: '#1B3A2D',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '24px', gap: '16px',
      }}>
        <p style={{ color: '#FACC15', fontWeight: 700, fontSize: '20px', margin: 0 }}>
          Something went wrong
        </p>

        {roundSaved ? (
          <>
            <p style={{ color: '#d1fae5', fontSize: '14px', margin: 0, textAlign: 'center', lineHeight: 1.5 }}>
              Your round is saved on this device.{'\n'}Reload to resume where you left off.
            </p>
            <button
              onClick={() => { window.location.href = '/?_r=' + Date.now(); }}
              style={{
                backgroundColor: '#FACC15', color: '#064E3B',
                border: 'none', borderRadius: '12px',
                padding: '14px 32px', fontWeight: 700, fontSize: '16px',
                cursor: 'pointer',
              }}
            >
              Reload &amp; Resume Round
            </button>
          </>
        ) : (
          <>
            <p style={{ color: '#fff', fontSize: '14px', margin: 0, textAlign: 'center' }}>
              {error.message || 'Unknown error'}
            </p>
            <button
              onClick={() => { window.location.href = '/?_r=' + Date.now(); }}
              style={{
                backgroundColor: '#FACC15', color: '#064E3B',
                border: 'none', borderRadius: '12px',
                padding: '12px 28px', fontWeight: 700, fontSize: '16px',
                cursor: 'pointer',
              }}
            >
              Reload App
            </button>
          </>
        )}

        <details style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', maxWidth: '340px', wordBreak: 'break-all' }}>
          <summary style={{ cursor: 'pointer' }}>Details</summary>
          <pre style={{ marginTop: '8px', whiteSpace: 'pre-wrap' }}>{error.stack}</pre>
        </details>
      </div>
    );
  }
}
